import HomeView from '@/views/HomeView.vue'
import BrowseView from '@/views/BrowseView.vue'
import ListingDetailView from '@/views/ListingDetailView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import type { RouteRecordRaw } from 'vue-router'

// The browse flow loads with the app; every other page is fetched on first visit,
// so a phone on a slow network downloads only what it opens.
const LoginView = () => import('@/views/auth/LoginView.vue')
const RegisterView = () => import('@/views/auth/RegisterView.vue')
const PiSignInCallbackView = () => import('@/views/auth/PiSignInCallbackView.vue')
const LinkPiWalletView = () => import('@/views/auth/LinkPiWalletView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const InboxView = () => import('@/views/InboxView.vue')
const BuyingView = () => import('@/views/dashboard/BuyingView.vue')
const SellingView = () => import('@/views/dashboard/SellingView.vue')
const DisputesView = () => import('@/views/dashboard/DisputesView.vue')
const BalanceView = () => import('@/views/dashboard/BalanceView.vue')
const OrderDetailView = () => import('@/views/OrderDetailView.vue')
const PayHandoverView = () => import('@/views/PayHandoverView.vue')
const ChatView = () => import('@/views/ChatView.vue')
const DisputeDetailView = () => import('@/views/DisputeDetailView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
const SettingsView = () => import('@/views/SettingsView.vue')
const CreateListingView = () => import('@/views/CreateListingView.vue')
const EditListingView = () => import('@/views/EditListingView.vue')
const AdminDashboardView = () => import('@/views/admin/DashboardView.vue')
const UsersView = () => import('@/views/admin/UsersView.vue')
const ListingsView = () => import('@/views/admin/ListingsView.vue')
const AdminDisputesView = () => import('@/views/admin/DisputesView.vue')
const AdminSettingsView = () => import('@/views/admin/SettingsView.vue')
const AdminOrdersView = () => import('@/views/admin/OrdersView.vue')
const AdminOrderDetailView = () => import('@/views/admin/OrderDetailView.vue')
const AdminPayoutsView = () => import('@/views/admin/PayoutsView.vue')
const AdminWalletView = () => import('@/views/admin/WalletView.vue')
const AdminCategoriesView = () => import('@/views/admin/CategoriesView.vue')
const ComponentGalleryView = () => import('@/views/dev/ComponentGalleryView.vue')
const StorefrontView = () => import('@/views/StorefrontView.vue')

declare module 'vue-router' {
  interface RouteMeta {
    /** Requires a signed-in user. */
    auth?: boolean
    /** Only for signed-out users (login, register). */
    guest?: boolean
    /** Requires the admin role. */
    admin?: boolean
    /** Requires the moderator or admin role (disputes, categories). */
    moderator?: boolean
  }
}

// Every route is a full page. Dashboard and admin sub-pages carry their own
// header or AdminShell, so they are flat siblings rather than nested children.
const auth = { auth: true }
const admin = { auth: true, admin: true }
const moderator = { auth: true, moderator: true }

const routes: RouteRecordRaw[] = [
  { path: '/', component: HomeView },
  { path: '/browse/', component: BrowseView },
  { path: '/listing/:id/', component: ListingDetailView },
  { path: '/u/:username/', component: StorefrontView },
  { path: '/login/', component: LoginView, meta: { guest: true } },
  { path: '/register/', component: RegisterView, meta: { guest: true } },
  { path: '/auth/pi-callback', component: PiSignInCallbackView },
  { path: '/link-wallet/', component: LinkPiWalletView, meta: auth },
  { path: '/inbox/', component: InboxView, meta: auth },
  { path: '/dashboard/', component: DashboardView, meta: auth },
  { path: '/dashboard/buying/', component: BuyingView, meta: auth },
  { path: '/dashboard/selling/', component: SellingView, meta: auth },
  { path: '/dashboard/disputes/', component: DisputesView, meta: auth },
  { path: '/dashboard/balance/', component: BalanceView, meta: auth },
  { path: '/order/:id/', component: OrderDetailView, meta: auth },
  // Public on purpose: a phone scanning the desktop QR code is usually signed out in its own browser.
  { path: '/pay/:id/', component: PayHandoverView },
  { path: '/chat/:orderId/', component: ChatView, meta: auth },
  { path: '/dispute/:id/', component: DisputeDetailView, meta: auth },
  { path: '/profile/:id?/', component: ProfileView, meta: auth },
  { path: '/settings/', component: SettingsView, meta: auth },
  { path: '/create-listing/', component: CreateListingView, meta: auth },
  { path: '/edit-listing/:id/', component: EditListingView, meta: auth },
  { path: '/admin/', component: AdminDashboardView, meta: admin },
  { path: '/admin/users/', component: UsersView, meta: admin },
  { path: '/admin/listings/', component: ListingsView, meta: admin },
  { path: '/admin/orders/', component: AdminOrdersView, meta: admin },
  { path: '/admin/orders/:id/', component: AdminOrderDetailView, meta: admin },
  { path: '/admin/payouts/', component: AdminPayoutsView, meta: admin },
  { path: '/admin/wallet/', component: AdminWalletView, meta: admin },
  { path: '/admin/categories/', component: AdminCategoriesView, meta: moderator },
  { path: '/admin/disputes/', component: AdminDisputesView, meta: moderator },
  { path: '/admin/disputes/:id/', component: DisputeDetailView, props: { moderation: true }, meta: moderator },
  { path: '/admin/settings/', component: AdminSettingsView, meta: admin },
  { path: '/dev/gallery/', component: ComponentGalleryView },
  { path: '/:pathMatch(.*)*', component: NotFoundView },
]

export default routes
